import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader } from "../components/ui/card";
import { Mail, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.success) {
        login(response.data.token, response.data.user);
        toast.success(`Welcome back, ${response.data.user.name}!`);
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      data-testid="login-page"
    >
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1754557177750-15e25cf42ff6?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-syncchain-bg via-syncchain-bg/95 to-syncchain-bg" />
      
      {/* Glow Effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-syncchain-accent/10 rounded-full blur-[150px]" />

      {/* Login Card */}
      <Card className="w-full max-w-md mx-4 bg-syncchain-paper/80 backdrop-blur-xl border-white/10 relative z-10 animate-slide-up">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_87e61a64-90d2-4c20-8ba6-31392dc5e379/artifacts/xzzkl8ic_SyncChain_White.png" 
              alt="SyncChain Logo" 
              className="h-16 w-auto mx-auto"
            />
          </div>
          <CardDescription className="text-syncchain-text-secondary font-mono text-xs uppercase tracking-wider mt-2">
            Rights & Metadata Resolver
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label 
                htmlFor="email" 
                className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary"
              >
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-syncchain-text-muted" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-10 h-12 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent focus:ring-syncchain-accent/20"
                  data-testid="email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label 
                htmlFor="password" 
                className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary"
              >
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-syncchain-text-muted" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pl-10 pr-10 h-12 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent focus:ring-syncchain-accent/20"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-syncchain-text-muted hover:text-syncchain-text"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full h-12 bg-syncchain-accent hover:bg-syncchain-accent-hover text-white font-semibold rounded-full transition-all duration-300 glow-hover"
              disabled={loading}
              data-testid="login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-syncchain-text-secondary">
              Don't have an account?{" "}
              <Link 
                to="/register" 
                className="text-syncchain-accent hover:text-syncchain-accent-hover font-medium"
              >
                Create one
              </Link>
            </p>
          </div>

          <p className="text-center text-xs text-syncchain-text-muted mt-6">
            Industry-grade rights management tool
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
